import { useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useUser } from '../contexts/UserContext'
import FoodBankDashboard from '../components/dashboards/FoodBankDashboard'
import SupplierDashboard from '../components/dashboards/SupplierDashboard'
import VolunteerDashboard from '../components/dashboards/VolunteerDashboard'
import AdminDashboard from '../components/dashboards/AdminDashboard'

const DashboardPage = () => {
  const { user, isAuthenticated, isLoading } = useUser()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true })
    }
  }, [isLoading, isAuthenticated, navigate])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated || !user) {
    return null
  }

  // Gates before rendering any dashboard (prevents map/dashboard flash)
  if (user.userType !== 'ADMIN' && !user.emailVerified) {
    return <Navigate to="/verify-email" replace />
  }

  if (!user.onboardingCompleted) {
    if (user.userType === 'FOOD_BANK') {
      return <Navigate to="/food-bank-onboarding" replace />
    }
    if (user.userType === 'SUPPLIER') {
      return <Navigate to="/supplier-onboarding" replace />
    }
    if (user.userType === 'VOLUNTEER') {
      return <Navigate to="/onboarding" replace />
    }
  }

  if (!user.userType) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md p-6 bg-white rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Authentication Error</h2>
          <p className="text-gray-700 mb-4">
            Unable to verify your account type. Please try logging out and logging back in.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            Refresh Page
          </button>
        </div>
      </div>
    )
  }

  switch (user.userType) {
    case 'FOOD_BANK':
      return <FoodBankDashboard />
    case 'SUPPLIER':
      return <SupplierDashboard />
    case 'VOLUNTEER':
      return <VolunteerDashboard />
    case 'ADMIN':
      return <AdminDashboard />
    default:
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center max-w-md p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Unknown account type</h2>
            <p className="text-gray-700 mb-4">
              Your account type ({user.userType}) is not supported. Please contact support.
            </p>
          </div>
        </div>
      )
  }
}

export default DashboardPage
